import { validate, ValidatorResult } from 'jsonschema';
import { isEqual, set } from 'lodash';
import { useCallback } from 'react';
import { Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type FieldToErrorMap = {
  [field: string]: { message: string; type: string };
};

export type CustomValidationRules = (data: unknown) => true | FieldToErrorMap;

const defaultCustomValidationRules: CustomValidationRules = () => true;

export const useJSONSchemaResolver = (
  jsonSchema: any,
  customValidationRules: CustomValidationRules = defaultCustomValidationRules
) => {
  const { t } = useTranslation();

  const resolver = useCallback<Resolver>(
    async (data) => {
      //we do null all empty strings as this allows us to send and validate date-times properly
      Object.keys(data).forEach((key) => {
        if (jsonSchema?.properties[key]?.format === 'date-time') {
          data[key] = data[key] === '' ? null : data[key];
        }
      });
      const customValidationResult = customValidationRules(data);

      let result: ValidatorResult | null = null;

      if (jsonSchema) {
        try {
          result = validate(data, jsonSchema);
        } catch (error) {
          console.error(error);
        }
      }

      //no json schema implies everything is allowed?
      if (
        (result === null || result.errors.length === 0) &&
        customValidationResult === true
      ) {
        return {
          values: data,
          errors: {},
        };
      }

      const customErrors =
        customValidationResult !== true ? { ...customValidationResult } : {};

      const errs = (result?.errors || []).reduce((allErrors, currentError) => {
        let dataPath = currentError.path.join('.');

        if (currentError.name === 'required') {
          //as ive learned, required is an error on the object level and therfore jut implicitly attributed to the field
          //RequiredParams
          dataPath = currentError.argument;
        }

        let customMessage: string;

        if (
          currentError.name === 'type' &&
          isEqual(currentError.argument, ['object'])
        ) {
          customMessage = t('form.validation.required');
        }

        set(allErrors, dataPath, {
          type: currentError.name ?? 'validation',
          message:
            customMessage ??
            t(`form.validation.${currentError.name}`, {
              replace: {
                ...currentError.argument,
                arg: currentError.argument,
              },
              defaultValue: currentError.message.replace(/^\w/, (c) =>
                c.toUpperCase()
              ),
            }),
        });

        return allErrors;
      }, customErrors);

      return {
        values: {},
        errors: errs,
      };
    },
    [customValidationRules, t, jsonSchema]
  );

  return resolver;
};

/**
 * Validates a single field value against a provided JSON Schema.
 * @param value - The value to validate.
 * @param schema - The JSON Schema for the value.
 * @returns An object with `valid` (boolean) and `errors` (jsonschema errors if any).
 */
export function validateField<T = unknown>(
  value: T,
  schema: Record<string, unknown>
): Pick<ValidatorResult, 'valid' | 'errors'> {
  return validate(value, schema);
}
